import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { queueAllCaseDeletions, queueCaseDeletions, saveCases } from "../storage";
import type { RealEstateCase } from "../types";
import { caseCollectionsMatch, syncCasesWithSupabase, type SyncStatus } from "./caseSync";
import {
  completeSupabasePasswordRecovery,
  deleteSupabasePushSubscription,
  getSupabasePushSubscriptionStatus,
  initializeSupabaseSession,
  isSupabaseConfigured,
  saveSupabasePushSubscription,
  requestSupabaseTestPush,
  signInWithSupabasePassword,
  signOutFromSupabase,
  updateSupabasePassword,
  type SupabaseAuthInitialization,
  type SupabasePushSubscription,
  type SupabasePushDeviceStatus,
  type SupabaseSession
} from "./supabase";

type UseCloudCaseSyncResult = {
  authReady: boolean;
  cloudConfigured: boolean;
  passwordRecovery: boolean;
  session: SupabaseSession | null;
  syncStatus: SyncStatus;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  completePasswordRecovery: () => void;
  storePushSubscription: (subscription: SupabasePushSubscription) => Promise<void>;
  removePushSubscription: (endpoint: string) => Promise<void>;
  getPushSubscriptionStatus: (endpoint: string) => Promise<SupabasePushDeviceStatus>;
  requestTestPush: () => Promise<string>;
  adoptSession: (session: SupabaseSession) => void;
  queueAllDeletions: (caseIds: string[]) => void;
  queueDeletion: (caseId: string) => void;
};

export const useCloudCaseSync = (
  cases: RealEstateCase[],
  setCases: Dispatch<SetStateAction<RealEstateCase[]>>
): UseCloudCaseSyncResult => {
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [session, setSession] = useState<SupabaseSession | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("Offline");
  const mountedRef = useRef(true);
  const casesRef = useRef(cases);
  const sessionRef = useRef<SupabaseSession | null>(null);
  const syncInFlightRef = useRef(false);
  const syncAgainRef = useRef(false);
  const initializationRef = useRef<Promise<SupabaseAuthInitialization> | null>(null);

  casesRef.current = cases;

  const runSync = useCallback(async () => {
    if (!isSupabaseConfigured || !sessionRef.current) {
      if (mountedRef.current) {
        setSyncStatus("Offline");
      }
      return;
    }

    if (syncInFlightRef.current) {
      syncAgainRef.current = true;
      return;
    }

    syncInFlightRef.current = true;

    do {
      syncAgainRef.current = false;
      if (mountedRef.current) {
        setSyncStatus("Syncing");
      }

      try {
        const result = await syncCasesWithSupabase(casesRef.current, sessionRef.current);
        if (!mountedRef.current) {
          break;
        }

        if (result.session.accessToken !== sessionRef.current.accessToken) {
          sessionRef.current = result.session;
          setSession(result.session);
        }

        if (!caseCollectionsMatch(casesRef.current, result.cases)) {
          casesRef.current = result.cases;
          saveCases(result.cases);
          setCases(result.cases);
        }

        setSyncStatus("Synced");
      } catch {
        if (mountedRef.current) {
          setSyncStatus("Offline");
        }
      }
    } while (syncAgainRef.current && mountedRef.current);

    syncInFlightRef.current = false;
  }, [setCases]);

  useEffect(() => {
    mountedRef.current = true;

    if (!isSupabaseConfigured) {
      setAuthReady(true);
      setSyncStatus("Offline");
      return () => {
        mountedRef.current = false;
      };
    }

    initializationRef.current ??= initializeSupabaseSession();
    initializationRef.current
      .then((initialization) => {
        if (!mountedRef.current) {
          return;
        }

        sessionRef.current = initialization.session;
        setSession(initialization.session);
        setPasswordRecovery(initialization.passwordRecovery);
      })
      .finally(() => {
        if (mountedRef.current) {
          setAuthReady(true);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (authReady && session) {
      sessionRef.current = session;
      void runSync();
    }
  }, [authReady, cases, runSync, session]);

  useEffect(() => {
    const handleOnline = () => void runSync();
    const handleOffline = () => setSyncStatus("Offline");
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void runSync();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [runSync]);

  const logout = useCallback(async () => {
    const currentSession = sessionRef.current;
    try {
      if (currentSession) {
        await signOutFromSupabase(currentSession);
      }
    } catch {
      // Local logout must still work while Supabase is unavailable.
    } finally {
      sessionRef.current = null;
      setSession(null);
      setPasswordRecovery(false);
      setSyncStatus("Offline");
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const signedInSession = await signInWithSupabasePassword(email, password);
    sessionRef.current = signedInSession;
    setSession(signedInSession);
    setPasswordRecovery(false);
    setAuthReady(true);
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const currentSession = sessionRef.current;
    if (!currentSession) {
      throw new Error("Recovery session už nie je platná. Pošli si nový odkaz na zmenu hesla.");
    }

    const updatedSession = await updateSupabasePassword(currentSession, password);
    sessionRef.current = updatedSession;
    setSession(updatedSession);
  }, []);

  const completePasswordRecovery = useCallback(() => {
    completeSupabasePasswordRecovery();
    setPasswordRecovery(false);
  }, []);

  const storePushSubscription = useCallback(async (subscription: SupabasePushSubscription) => {
    const currentSession = sessionRef.current;
    if (!currentSession) {
      throw new Error("Pre upozornenia sa najprv prihlás do FISPRO OS.");
    }

    const updatedSession = await saveSupabasePushSubscription(subscription, currentSession);
    if (updatedSession.accessToken !== currentSession.accessToken) {
      sessionRef.current = updatedSession;
      setSession(updatedSession);
    }
  }, []);

  const removePushSubscription = useCallback(async (endpoint: string) => {
    const currentSession = sessionRef.current;
    if (!currentSession) {
      return;
    }

    const updatedSession = await deleteSupabasePushSubscription(endpoint, currentSession);
    if (updatedSession.accessToken !== currentSession.accessToken) {
      sessionRef.current = updatedSession;
      setSession(updatedSession);
    }
  }, []);

  const getPushSubscriptionStatus = useCallback(async (endpoint: string) => {
    const currentSession = sessionRef.current;
    if (!currentSession) return { registered: false };
    const result = await getSupabasePushSubscriptionStatus(endpoint, currentSession);
    if (result.session.accessToken !== currentSession.accessToken) {
      sessionRef.current = result.session;
      setSession(result.session);
    }
    return result.status;
  }, []);

  const requestTestPush = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession) throw new Error("Pre test notifikácie sa najprv prihlás do FISPRO OS.");
    const result = await requestSupabaseTestPush(currentSession);
    if (result.session.accessToken !== currentSession.accessToken) {
      sessionRef.current = result.session;
      setSession(result.session);
    }
    return result.requestId;
  }, []);

  const adoptSession = useCallback((nextSession: SupabaseSession) => {
    if (sessionRef.current?.user.id !== nextSession.user.id) {
      return;
    }

    sessionRef.current = nextSession;
    setSession((current) => current?.accessToken === nextSession.accessToken ? current : nextSession);
  }, []);

  const queueAllDeletions = useCallback((caseIds: string[]) => {
    const deletedAt = new Date().toISOString();
    queueCaseDeletions(caseIds, deletedAt);
    queueAllCaseDeletions(deletedAt);
  }, []);

  const queueDeletion = useCallback((caseId: string) => {
    queueCaseDeletions([caseId], new Date().toISOString());
  }, []);

  return {
    authReady,
    cloudConfigured: isSupabaseConfigured,
    passwordRecovery,
    session,
    syncStatus,
    login,
    logout,
    updatePassword,
    completePasswordRecovery,
    storePushSubscription,
    removePushSubscription,
    getPushSubscriptionStatus,
    requestTestPush,
    adoptSession,
    queueAllDeletions,
    queueDeletion
  };
};
